import React from 'react';
import { AppState, Platform } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { useGetUnreadConversationsCountQuery } from '@/store/api/admin/messagesApi';

const POLLING_INTERVAL = 60_000; // 60 seconds for more responsive notifications
const ERROR_BACKOFF_DURATION = 60_000; // 60 seconds backoff after error

/**
 * Hook that returns the unread property chat conversations count.
 * Uses polling and respects app state (foreground/background).
 * Includes error backoff to prevent spamming server on persistent errors.
 */
export const useUnreadPropertyChatCount = () => {
  const isFocused = useIsFocused();
  const appStateRef = React.useRef<string>(AppState.currentState);
  
  // Error backoff state - timestamp until which polling is paused after an error
  const errorBackoffUntilRef = React.useRef<number>(0);
  const [shouldSkipForBackoff, setShouldSkipForBackoff] = React.useState(false);

  // Track app visibility
  React.useEffect(() => {
    const sub = AppState.addEventListener('change', (state) => {
      appStateRef.current = state;
    });
    return () => sub.remove();
  }, []);

  const isEnvironmentActive = React.useCallback(() => {
    if (Platform.OS === 'web') {
      try {
        const doc: any = typeof globalThis !== 'undefined' ? (globalThis as any).document : null;
        const isVisible = doc ? doc.visibilityState === 'visible' : true;
        const isDocFocused = doc && typeof doc.hasFocus === 'function' ? doc.hasFocus() : true;
        return isVisible && isDocFocused;
      } catch {
        return true;
      }
    }
    return appStateRef.current === 'active';
  }, []);

  const { data, refetch, isError } = useGetUnreadConversationsCountQuery(undefined, {
    pollingInterval: POLLING_INTERVAL,
    skip: !isEnvironmentActive() || shouldSkipForBackoff,
    refetchOnMountOrArgChange: true, // Ensure fresh data when component remounts (e.g., after closing conversation modal)
  });

  // Handle error backoff - pause polling after errors to prevent server spam
  React.useEffect(() => {
    if (isError) {
      // Set backoff timestamp
      errorBackoffUntilRef.current = Date.now() + ERROR_BACKOFF_DURATION;
      setShouldSkipForBackoff(true);
      
      // Schedule resume after backoff period
      const timeoutId = setTimeout(() => {
        errorBackoffUntilRef.current = 0;
        setShouldSkipForBackoff(false);
      }, ERROR_BACKOFF_DURATION);
      
      return () => clearTimeout(timeoutId);
    } else {
      // Reset backoff on success
      if (errorBackoffUntilRef.current > 0) {
        errorBackoffUntilRef.current = 0;
        setShouldSkipForBackoff(false);
      }
    }
  }, [isError]);

  // Refetch when screen is focused (respecting backoff)
  React.useEffect(() => {
    if (isFocused && isEnvironmentActive() && !shouldSkipForBackoff && Date.now() >= errorBackoffUntilRef.current) {
      refetch();
    }
  }, [isFocused, refetch, isEnvironmentActive, shouldSkipForBackoff]);

  return data?.count || 0;
};

export default useUnreadPropertyChatCount;
